import {
  AddonTriggerContext,
  AddonTriggerEvents,
  Box,
  Button,
  DropdownButtonContext,
  FlexBox,
  IconButton,
  InputRichText,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { HookFormText } from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import {
  ContextType,
  KeyboardEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useMount, useUnmount } from 'react-use';

import { automationsApi } from 'apis/automations';
import { templatesApi } from 'apis/templates';
import {
  convertPlainToHtml,
  getDirectionFormattedHtml,
} from 'app/utils/htmlFormatting';
import supportedLanguages, { Language } from 'app/utils/supportedLanguages';
import { AiLoadingState } from 'components/aiLoadingState';
import {
  featureFlagList as automationFeatureFlagList,
  channelOptions,
} from 'components/automations/components/automations.constants';
import { getComputedOptions } from 'components/automations/components/automations.utils';
import { ChangeSubjectFn } from 'components/editorQuickRepliesDropdown/editorQuickRepliesDropdown.types';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';
import { useServerStorage } from 'utils/useServerStorage';

import { ConversationContext } from '../../../conversation';

import { LanguageSelector } from './components/languageSelector/languageSelector';
import { QuickRepliesPreview } from './components/quickRepliesPreview/quickRepliesPreview';
import { TemplateList } from './components/templateList/templateList';
import styles from './quickRepliesContent.module.scss';
import { getLanguageSpecificSubject } from './util/getLanguageSpecificContent';
import {
  EnhancedTemplate,
  isEnhancedTemplate,
  QuickReplyType,
  useQuickReplyData,
} from './util/useQuickReplyData';

type QuickRepliesContentProps = {
  /**
   * The editor quick response search trigger that triggers the quick reply
   * dropdown, null if triggered via toolbarAddon
   */
  editorSearchTrigger?: string | null;

  /** Custom function to insert the content of the quick reply into the editor */
  insertContent?: (content: string) => void;
  /** Callback to change the email subject */
  changeSubject?: ChangeSubjectFn;
  /** Event emitter */
  eventEmitter?: ContextType<typeof AddonTriggerContext>['eventEmitter'];
  /** Enduser ID */
  enduserId?: string | null;
  /**
   * Conversation ID — overrides ConversationContext when provided (e.g. compose
   * modal)
   */
  csid?: number | null;
};

type Form = {
  searchTerm: string;
  favoriteSearchTerm: string;
};

enum Mode {
  Normal = 'normal',
  Favorites = 'favorites',
}

export const QuickRepliesContent = ({
  editorSearchTrigger = null,
  insertContent,
  changeSubject,
  eventEmitter,
  enduserId = null,
  csid = null,
}: QuickRepliesContentProps): JSX.Element => {
  const { hasCopilot } = useCopilotTrial();
  const isAddonTriggerMode = !!editorSearchTrigger;
  const editorContext = useEditorContext();
  const storage = useServerStorage('user');
  const richtextContext = useContext(InputRichText.Context);
  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'templates-language-translation-control',
    ...automationFeatureFlagList,
  ]);

  const [runAutomation] = automationsApi.useRunAutomationMutation();
  const [renderTemplate] = templatesApi.useRenderTemplateMutation();
  const showPreview = storage.data.conversation?.quickReplyShowPreview ?? false;

  const [selectedTemplate, setSelectedTemplate] = useState<
    EnhancedTemplate | undefined
  >();

  const [showFavorites, toggleFavorites] = useState(false);

  const { conversation } = useContext(ConversationContext);
  const channel = conversation?.initial_channel;
  const isAutomationSupportedChannel = useMemo(() => {
    const automationChannelOptionsComputed = getComputedOptions(
      channelOptions,
      flags,
    );
    return automationChannelOptionsComputed.some(
      (option) =>
        option.id === channel ||
        (channel && (option.alternateIds || []).includes(channel)),
    );
  }, [channel, flags]);

  const focusRef = useRef<HTMLElement>(null);

  const focusQuickReplies = useCallback(() => {
    if (focusRef.current && focusRef.current instanceof HTMLElement) {
      const listElement = focusRef.current.querySelector(
        '#recommended-quick-replies-template-selector',
      );
      if (listElement && listElement instanceof HTMLElement) {
        listElement.focus();
      }
    }
  }, []);

  const { closeDropdown } = useContext(DropdownButtonContext);

  const languageCode =
    conversation?.attributes && 'language' in conversation.attributes
      ? conversation.attributes.language
      : undefined;

  const defaultLanguage = useMemo(
    () =>
      supportedLanguages.find(
        (language) => language.code === 'en',
      ) as (typeof supportedLanguages)[number],
    [],
  );

  const {
    data: { data: { locales: localeData } } = {
      data: { locales: [] as string[] },
    },
    isLoading: localeIsLoading,
  } = templatesApi.useLocalesQuery(
    { type: 'quick_response' },
    {
      skip: !flags.templatesLanguageTranslationControl,
    },
  );

  const [selectedLanguage, setSelectedLanguage] = useState(
    flags.templatesLanguageTranslationControl
      ? (supportedLanguages
          .filter((language) => localeData.includes(language.code))
          .find((language) => language.code === languageCode) ??
          defaultLanguage)
      : defaultLanguage,
  );

  const locale = selectedLanguage?.code ?? 'en';
  const localeName = selectedLanguage?.label ?? 'English';

  useEffect(() => {
    // if selectedLanguage is not in in the list of available languages, reset it
    if (
      selectedLanguage &&
      !localeData.includes(selectedLanguage.code) &&
      selectedLanguage.code !== defaultLanguage.code
    ) {
      setSelectedLanguage(defaultLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeData]);

  const initialSearchTerm = useMemo(() => {
    if (isAddonTriggerMode) {
      return editorSearchTrigger.slice(1);
    }
    return '';
  }, [isAddonTriggerMode, editorSearchTrigger]);

  const { control, watch, setValue } = useForm<Form>({
    defaultValues: {
      searchTerm: initialSearchTerm,
    },
  });

  const searchTerm = watch('searchTerm');
  const favoriteSearchTerm = watch('favoriteSearchTerm');

  const memoizedSearchValue = useMemo(() => {
    if (isAddonTriggerMode) {
      return editorSearchTrigger.slice(1);
    }
    return searchTerm;
  }, [isAddonTriggerMode, editorSearchTrigger, searchTerm]);

  const isSearching = memoizedSearchValue.length > 0;

  /**
   * Applies the selected template to the editor's content. If a template is
   * passed as an argument, it will be used instead of the selected template.
   *
   * Renders the content and subject of the template, and inserts the content
   * into the editor as needed.
   */
  const applyTemplate = useCallback(
    async (overridenTemplate?: EnhancedTemplate) => {
      const templateToBeApplied = isEnhancedTemplate(overridenTemplate)
        ? overridenTemplate
        : selectedTemplate;

      if (!templateToBeApplied) {
        return;
      }

      richtextContext.content.setUiBlocker(
        <Box style={{ position: 'absolute', bottom: 16 }}>
          <AiLoadingState label="Applying template" />
        </Box>,
      );

      const subject = getLanguageSpecificSubject(locale, templateToBeApplied);
      const automationId = templateToBeApplied.automation_id;
      const templateHasAutomation = !!automationId;
      const shouldRunAutomation =
        isAutomationSupportedChannel &&
        templateHasAutomation &&
        typeof conversation?.id === 'number';

      const contentResponse = await renderTemplate({
        templateId: templateToBeApplied.id,
        locale,
        csid: csid ?? conversation?.id ?? null,
        subject: subject || null,
        enduserId,
      }).unwrap();

      ampli.quickReplyChosen({
        sourceList:
          templateToBeApplied.sourceList === 'all' && isSearching
            ? 'search'
            : templateToBeApplied.sourceList,
        score: templateToBeApplied.score,
        macroRan: templateHasAutomation,
      });
      dispatch(
        templatesApi.endpoints.trackTemplateUsage.initiate({
          templateId: templateToBeApplied.id,
          locale,
          csid: (csid ?? conversation?.id)?.toString(),
        }),
      );

      if (subject && typeof changeSubject === 'function') {
        changeSubject?.(subject || '');
      }

      if (typeof insertContent === 'function') {
        // Use the trigger insertContent function, as it will handle replacing
        // the trigger text with the content
        insertContent?.(
          sanitizeHtml(contentResponse.data.rendered || '', 'email'),
        );
      } else {
        // Otherwise, insert the content directly into the editor at the current
        // cursor position
        editorContext.insert(
          getDirectionFormattedHtml(
            convertPlainToHtml(contentResponse.data.rendered || ''),
          ),
        );
      }

      if (shouldRunAutomation && conversation?.id) {
        runAutomation({
          automationId,
          csid: conversation?.id,
        });
      }

      closeDropdown();
      richtextContext.content.setUiBlocker(null);
    },
    [
      selectedTemplate,
      richtextContext.content,
      locale,
      dispatch,
      isSearching,
      conversation?.id,
      changeSubject,
      insertContent,
      isAutomationSupportedChannel,
      closeDropdown,
      editorContext,
      runAutomation,
      renderTemplate,
      enduserId,
    ],
  );

  /** Event handler for applying the selected template to the editor. */
  const handleApplyTemplate = useCallback(
    (_event?: Event) => {
      if ((_event as MouseEvent)?.detail !== 2) {
        /** On double click, don't apply on the second click */
        applyTemplate();
      }
    },
    [applyTemplate],
  );

  useMount(() => {
    if (eventEmitter) {
      eventEmitter.addEventListener(
        AddonTriggerEvents.FOCUS,
        focusQuickReplies,
      );

      // Selection list handles arrow keys by default if list is "interactable" (active)
      eventEmitter.addEventListener(AddonTriggerEvents.DOWN, focusQuickReplies);
      eventEmitter.addEventListener(AddonTriggerEvents.UP, focusQuickReplies);
    }
  });

  useUnmount(() => {
    if (eventEmitter) {
      eventEmitter.clearEventListeners(AddonTriggerEvents.FOCUS);
      eventEmitter.clearEventListeners(AddonTriggerEvents.DOWN);
      eventEmitter.clearEventListeners(AddonTriggerEvents.UP);
    }
  });

  useEffect(() => {
    if (eventEmitter) {
      eventEmitter.clearEventListeners(AddonTriggerEvents.INSERT);
      eventEmitter.addEventListener(
        AddonTriggerEvents.INSERT,
        handleApplyTemplate,
      );
    }
    return () => {
      if (eventEmitter) {
        eventEmitter.clearEventListeners(AddonTriggerEvents.INSERT);
      }
    };
  }, [eventEmitter, handleApplyTemplate]);

  const toggleShowFavorites = useCallback(() => {
    const nextShowFavValue = !showFavorites;

    toggleFavorites(nextShowFavValue);
    if (nextShowFavValue) {
      ampli.quickReplyFilterChosen({
        filterUsed: 'favorites',
      });
    }
  }, [showFavorites]);

  const mode = useMemo(() => {
    if (showFavorites) return Mode.Favorites;
    return Mode.Normal;
  }, [showFavorites]);

  const searchFieldName = useMemo(() => {
    switch (mode) {
      case Mode.Favorites:
        return 'favoriteSearchTerm';
      case Mode.Normal:
        return 'searchTerm';
    }
  }, [mode]);

  const filteredLanguages = useMemo(() => {
    return localeData
      .map((code) => {
        const language = supportedLanguages.find((l) => l.code === code);
        return language ?? { code, label: code };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [localeData]);

  useEffect(() => {
    // when mode changes, reset the search terms
    setValue('favoriteSearchTerm', '');
  }, [mode, setValue]);

  const searchFieldPlaceholder = useMemo(() => {
    switch (mode) {
      case Mode.Favorites:
        return 'Search favorites';
      case Mode.Normal:
      default:
        return 'Search for a reply';
    }
  }, [mode]);
  const shouldDisplaySearchField = useMemo(() => {
    if (isAddonTriggerMode) {
      return mode !== Mode.Normal;
    }
    return true;
  }, [isAddonTriggerMode, mode]);
  const showRecommendations = hasCopilot && conversation?.id && !isSearching;
  const showMostUsed = !isSearching;

  const needsAll = mode !== Mode.Favorites;
  const needsRecommendations = showRecommendations && mode !== Mode.Favorites;
  const needsMostUsed = showMostUsed && mode !== Mode.Favorites;

  const neededLists = useMemo(() => {
    return [
      QuickReplyType.All,
      QuickReplyType.Favorites,
      QuickReplyType.MostUsed,
      QuickReplyType.Recommendations,
    ].filter((type) => {
      switch (type) {
        case QuickReplyType.Recommendations:
          return needsRecommendations;
        case QuickReplyType.MostUsed:
          return needsMostUsed;
        case QuickReplyType.Favorites:
          return showFavorites;
        case QuickReplyType.All:
          return needsAll;
      }
    });
  }, [needsAll, needsMostUsed, needsRecommendations, showFavorites]);

  const {
    recommendedTemplates,
    mostUsedTemplates,
    allTemplates,
    favoriteTemplates,
    toggleFavorite,
  } = useQuickReplyData(
    neededLists,
    locale,
    conversation?.id?.toString() || '',
    memoizedSearchValue,
    favoriteSearchTerm,
  );

  useEffect(() => {
    // Update selection as lists change
    const recommendedItems =
      needsRecommendations && !recommendedTemplates.isLoading
        ? recommendedTemplates.items
        : [];
    const mostUsedItems =
      needsMostUsed && !mostUsedTemplates.isLoading
        ? mostUsedTemplates.items
        : [];
    const allItems =
      needsAll && !allTemplates.isLoading ? allTemplates.items : [];
    const favoriteItems =
      showFavorites && !favoriteTemplates.isLoading
        ? favoriteTemplates.items
        : [];

    const availableItems = [
      ...recommendedItems,
      ...mostUsedItems,
      ...allItems,
      ...favoriteItems,
    ];

    if (
      selectedTemplate &&
      !availableItems.find((t) => t.id === selectedTemplate.id)
    ) {
      // if selected template is not in any of the lists, reset it
      setSelectedTemplate(undefined);
    }

    if (!selectedTemplate && availableItems.length > 0 && showPreview) {
      // if no template is selected, select the first one
      setSelectedTemplate(availableItems[0]);
    }
    if (!showPreview) {
      // if preview is off, reset selected template
      setSelectedTemplate(undefined);
    }
  }, [
    showPreview,
    allTemplates,
    favoriteTemplates,
    mostUsedTemplates,
    recommendedTemplates,
    selectedTemplate,
    needsAll,
    showFavorites,
    showMostUsed,
    showRecommendations,
    needsRecommendations,
    needsMostUsed,
  ]);

  const hasLoadedOneRelevantList =
    (needsRecommendations && !recommendedTemplates.isLoading) ||
    (needsMostUsed && !mostUsedTemplates.isLoading) ||
    (needsAll && !allTemplates.isLoading) ||
    (showFavorites && !favoriteTemplates.isLoading);

  const handleListChange = (
    list: SelectionListChangeEvent<EnhancedTemplate>,
  ) => {
    if (
      showPreview &&
      !(selectedTemplate && list.selectedItem.value.id === selectedTemplate.id)
    ) {
      // If preview is on, only select, don't apply the template
      setSelectedTemplate(list.selectedValues[0]);
    } else if (list.event?.detail !== 2) {
      /**
       * Default behavior, apply the template. On double click, don't apply on
       * the second click
       */
      list?.event?.preventDefault();
      applyTemplate(list.selectedValues[0]);
    }
  };

  const toggleShowPreview = useCallback(() => {
    storage.set({
      conversation: (draft) => {
        draft.quickReplyShowPreview = !showPreview;
      },
    });
  }, [showPreview, storage]);

  const onKeyPress: KeyboardEventHandler<HTMLOrSVGElement> = useCallback(
    (event) => {
      // Press "P" to toggle preview
      if (event.key === 'p') {
        toggleShowPreview();
      }
      // Press "F" to toggle favorites
      if (event.key === 'f') {
        toggleShowFavorites();
      }
    },
    [toggleShowFavorites, toggleShowPreview],
  );

  return (
    <FlexBox height="320px" minWidth="368px" ref={focusRef as any}>
      {flags.templatesLanguageTranslationControl && (
        <FlexBox>
          <LanguageSelector
            languages={filteredLanguages as Language[]}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            isLoading={localeIsLoading}
          />
        </FlexBox>
      )}
      <FlexBox
        flexDirection="column"
        padding="300 300 100 300"
        width="360px"
        gap="8px"
      >
        <FlexBox flexDirection="row" alignItems="center" gap="8px">
          {shouldDisplaySearchField && (
            <HookFormText
              key={searchFieldName}
              name={searchFieldName}
              control={control}
              componentProps={{
                icon: 'search',
                placeholder: searchFieldPlaceholder,
                tabIndex: -1,
                accessKey: 's',
                autoFocus: mode === Mode.Normal,
              }}
              noLabel
              noAlert
            />
          )}
          {(!shouldDisplaySearchField && (
            <>
              <Button
                icon="star"
                variant="secondary"
                size="large"
                onClick={toggleShowFavorites}
                className={classNames({
                  [styles.filterButton]: true,
                  [styles.filterButtonWide]: true,
                })}
                tabIndex={-1}
                accessKey="f"
              >
                Show favorites
              </Button>
              <Button
                icon={showPreview ? 'eye-closed' : 'eye'}
                variant={showPreview ? 'primary' : 'secondary'}
                size="large"
                onClick={toggleShowPreview}
                className={classNames({
                  [styles.filterButton]: true,
                  [styles.filterButtonWide]: true,
                })}
                tabIndex={-1}
                accessKey="p"
              >
                {showPreview ? 'Hide preview' : 'Show preview'}
              </Button>
            </>
          )) || (
            <>
              <IconButton
                icon={showFavorites ? 'star-full' : 'star'}
                variant={showFavorites ? 'primary' : 'secondary'}
                size="large"
                onClick={toggleShowFavorites}
                className={styles.filterButton}
                tabIndex={-1}
                accessKey="f"
              />
              <IconButton
                icon={showPreview ? 'eye-closed' : 'eye'}
                variant={showPreview ? 'primary' : 'secondary'}
                size="large"
                onClick={toggleShowPreview}
                className={styles.filterButton}
                tabIndex={-1}
                accessKey="p"
              />
            </>
          )}
        </FlexBox>

        <SelectionList
          id="recommended-quick-replies-template-selector"
          className={styles.list}
          onChange={handleListChange}
          equalityFn={(a, b) => {
            return a && b && a.id === b.id;
          }}
          selectedValues={selectedTemplate ? [selectedTemplate] : undefined}
          onKeyPress={onKeyPress}
        >
          {mode === Mode.Favorites && (
            <TemplateList
              sectionTitle="Favorites"
              templates={favoriteTemplates.items}
              isLoading={favoriteTemplates.isLoading}
              isError={favoriteTemplates.isError}
              errorMessage="Failed to get favorites."
              emptyMessage={`No favorites found (${localeName})`}
              emptyDescription="Try favoriting some templates."
              toggleFavorite={toggleFavorite}
              searchTerm={memoizedSearchValue}
              locale={locale}
              onApplyTemplate={handleApplyTemplate}
              showAutomation={isAutomationSupportedChannel && !showPreview}
            />
          )}
          {mode === Mode.Normal && (
            <>
              {showRecommendations && (
                <TemplateList
                  sectionTitle="Recommended for this conversation"
                  templates={recommendedTemplates.items}
                  isLoading={recommendedTemplates.isLoading}
                  isError={recommendedTemplates.isError}
                  errorMessage="Failed to get recommendations."
                  loadingMessage="Searching for the best replies"
                  loadingHeight="96px"
                  toggleFavorite={toggleFavorite}
                  locale={locale}
                  onApplyTemplate={handleApplyTemplate}
                  showAutomation={isAutomationSupportedChannel && !showPreview}
                />
              )}
              {showMostUsed && (
                <TemplateList
                  sectionTitle="Frequently used"
                  templates={mostUsedTemplates.items}
                  isLoading={mostUsedTemplates.isLoading}
                  isError={mostUsedTemplates.isError}
                  errorMessage="Failed to get most frequently used replies."
                  toggleFavorite={toggleFavorite}
                  locale={locale}
                  onApplyTemplate={handleApplyTemplate}
                  showAutomation={isAutomationSupportedChannel && !showPreview}
                />
              )}
              <TemplateList
                sectionTitle={isSearching ? 'Search results' : 'All replies'}
                templates={allTemplates.items}
                isLoading={allTemplates.isLoading}
                isError={allTemplates.isError}
                errorMessage="Failed to get any templates."
                emptyMessage={
                  isSearching ? 'No templates found' : 'No templates available'
                }
                emptyDescription={
                  isSearching ? 'Try changing your search criteria.' : undefined
                }
                hasNextPage={allTemplates.hasNextPage}
                nextPage={allTemplates.nextPage}
                toggleFavorite={toggleFavorite}
                searchTerm={memoizedSearchValue}
                locale={locale}
                onApplyTemplate={handleApplyTemplate}
                showAutomation={isAutomationSupportedChannel && !showPreview}
              />
            </>
          )}
        </SelectionList>
      </FlexBox>
      <FlexBox>
        {showPreview && (
          <QuickRepliesPreview
            template={selectedTemplate}
            language={locale}
            isLoading={!hasLoadedOneRelevantList}
            onApplyTemplate={handleApplyTemplate}
            showAutomation={
              isAutomationSupportedChannel && !!selectedTemplate?.automation_id
            }
            csid={conversation?.id || null}
            enduserId={enduserId || null}
          />
        )}
      </FlexBox>
    </FlexBox>
  );
};
